/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable} from 'mobx';
import {observer, inject} from 'mobx-react';
import {Link, withRouter, locationShape} from 'react-router';

import autobind from 'autobind-decorator';
import {cn} from 'spa/css';

import LogoNew from 'spa/Components/Footer/LogoNew';
import CountrySelect from 'spa/Components/Header/CountrySelect';
import ProgressiveImage from 'spa/Components/ProgressiveImage';
import {withConfig, withI18n} from 'spa/Components/Context';

import './landing-footer.less';

import Amex from 'spa/images/new-design/icons/amex.svg';
import ApplePay from 'spa/images/new-design/icons/applepay.svg';
import GooglePay from 'spa/images/new-design/icons/googlepay.svg';
import Mastercard from 'spa/images/new-design/icons/mastercard.svg';
import Paypal from 'spa/images/new-design/icons/paypal.svg';
import Visa from 'spa/images/new-design/icons/visa.svg';

const isServer = typeof window === 'undefined';
const {HERO_BASE_URL} = isServer ? process.env : window;

@withRouter
@inject(({store: {landingData, tracker}}) => (
  {
    landingData,
    tracker,
  }
))
@observer
@withI18n
@withConfig
class LandingFooter extends React.Component {
  static propTypes = {
    landingData: PropTypes.object,
    location: locationShape.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }),
  }

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {landingData} = this.props;

    if (landingData) {
      this.downloadAppsLink = config.getValue('locale.adjust-landing-page-app-url', i18n.umRegion);
    } else {
      this.downloadAppsLink = config.getValue('locale.adjust-app-url', i18n.umRegion);
    }

    this.communityUrl = config.getValue('b2c.community-url', i18n.umRegion);
    this.faqUrl = config.getValue('b2c.faq-url', i18n.umRegion);
    this.zendeskLocale = config.getValue('locale.zendesk-locale-id', i18n.umRegion);
    this.isShopEnabled = config.getValue('locale.shop-enabled', i18n.umRegion) || false;
    this.shopUrl = config.getValue('locale.shop-url', i18n.umRegion) || '';
    this.isBlogEnabled = config.getValue('locale.show-editorial', i18n.umRegion);
    this.blogUrl = config.getValue('locale.editorial-url', i18n.umRegion);
    this.showFRDisclaimer = config.getValue('locale.show-fr-disclaimer', i18n.umRegion);
    this.cityLinks = config.getValue('b2c.city-footer-link-list', i18n.umRegion);
    this.showCityList = config.getValue('b2c.show-city-list-footer', i18n.umRegion);
    this.digitalTreatmentsOnly = config.getValue('b2c.show-digital-specialities-only-footer', i18n.umRegion);
    this.footerYogaActive = config.getValue('b2c.show-digital-yoga-footer', i18n.umRegion);
    this.footerPTActive = config.getValue('b2c.show-digital-pt-footer', i18n.umRegion);
    this.footerMeditationActive = config.getValue('b2c.show-digital-meditation-footer', i18n.umRegion);

    this.facebookUrl = config.getValue('locale.social.facebook-url', i18n.umRegion);
    this.instagramUrl = config.getValue('locale.social.instagram-url', i18n.umRegion);
    this.linkedinUrl = config.getValue('locale.social.linkedin-url', i18n.umRegion);
    this.twitterUrl = config.getValue('locale.social.twitter-url', i18n.umRegion);
  }

  @autobind
  @action('clickedCommunityLink - LandingFooter')
  clickedCommunityLink(event) {
    const {tracker} = this.props;

    tracker.track('Clicked community link in the footer', {
      category: 'Community site link',
    });
  }

  @computed get renderCityLinks() {
    const {i18n} = this.context;
    const {cityLinks} = this;
    if (!cityLinks) return null;
    return cityLinks.map(city => (
      <li key={city}>
        <a
          className="landing-footer__section-link"
          href={`/${i18n.lc}/locations/${city.toLowerCase()}`}
          title={city}
        >
          {city}
        </a>
      </li>
    )).concat();
  }

  @computed get digitalFooterLinks() {
    const {i18n} = this.context;
    const {
      footerYogaActive,
      footerPTActive,
      footerMeditationActive,
    } = this;

    const digitalLinks = [];

    if (footerYogaActive) {
      digitalLinks.push([
        <li key="landing-footer-speciality-link-1">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-yoga/beginners_yoga_25`}>
            {i18n._x('Beginner\'s yoga', 'footer link')}
          </Link>
        </li>,
        <li key="landing-footer-speciality-link-2">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-yoga/power_yoga_55`}>
            {i18n._x('Power yoga', 'footer link')}
          </Link>
        </li>,
        <li key="landing-footer-speciality-link-3">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-yoga/wind_down_yoga_55`}>
            {i18n._x('Wind down yoga', 'footer link')}
          </Link>
        </li>,
        <li key="landing-footer-speciality-link-4">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-yoga/rise_and_shine_yoga_25`}>
            {i18n._x('Rise and shine yoga', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    if (footerPTActive) {
      digitalLinks.push([
        <li key="landing-footer-speciality-link-5">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-pt/stretch-25`}>
            {i18n._x('Stretch workout', 'footer link')}
          </Link>
        </li>,
        <li key="landing-footer-speciality-link-6">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/digital-pt/full_body_workout_25`}>
            {i18n._x('Full body workout', 'footer link')}
          </Link>
        </li>,
        <li key="landing-footer-speciality-link-7">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-pt/strength_55`}>
            {i18n._x('Strength workout', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    if (footerMeditationActive && i18n.umRegion === 'UK') {
      digitalLinks.push([
        <li key="landing-footer-speciality-link-8">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-meditation/morning_meditation_20`}>
            {i18n._x('Morning meditation', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    if (footerMeditationActive && i18n.umRegion === 'FR') {
      digitalLinks.push([
        <li key="landing-footer-speciality-link-8">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/digital-meditation-fr/eveil_matinal_20`}>
            {i18n._x('Morning meditation', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    return digitalLinks;
  }

  @computed get physicalFooterThirdLink() {
    const {i18n} = this.context;

    if (i18n.umRegion === 'UK') {
      return ([
        <li key="landing-footer-speciality-link-physical-3">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/massage/sports-remedial-massage`}>
            {i18n._x('Sports massage', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    return ([
      <li key="landing-footer-speciality-link-physical-3">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/massage/thai-yoga-massage`}>
          {i18n._x('Thai massage', 'footer link')}
        </Link>
      </li>,
    ]);
  }

  @computed get physicalFooterLastLink() {
    const {i18n} = this.context;

    if (i18n.umRegion === 'UK') {
      return ([
        <li key="landing-footer-speciality-link-physical-last">
          <Link
            className="landing-footer__section-link"
            to={`/${i18n.lc}/treatments/physiotherapy`}>
            {i18n._x('Physiotherapy consultation & treatment', 'footer link')}
          </Link>
        </li>,
      ]);
    }

    return [];
  }

  @computed get physicalFooterLinks() {
    const {i18n} = this.context;
    const {digitalTreatmentsOnly, physicalFooterLastLink, physicalFooterThirdLink} = this;

    if (digitalTreatmentsOnly) return null;

    return ([
      <li key="landing-footer-speciality-link-physical-1">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/massage/urban-classic-swedish-massage`}>
          {i18n._x('Classic massage', 'footer link')}
        </Link>
      </li>,
      <li key="landing-footer-speciality-link-physical-1">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/massage/deep-tissue-massage`}>
          {i18n._x('Deep tissue massage', 'footer link')}
        </Link>
      </li>,
      physicalFooterThirdLink,
      <li key="landing-footer-speciality-link-physical-4">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/massage/reflexology-foot-massage`}>
          {i18n._x('Reflexology massage', 'footer link')}
        </Link>
      </li>,
      <li key="landing-footer-speciality-link-physical-5">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/nails/luxury-manicure`}>
          {i18n._x('Luxury Manicure', 'footer link')}
        </Link>
      </li>,
      <li key="landing-footer-speciality-link-physical-6">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/nails/gel-polish-manicure`}>
          {i18n._x('Gel polish manicure', 'footer link')}
        </Link>
      </li>,
      <li key="landing-footer-speciality-link-physical-7">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/nails/luxury-mani-pedi`}>
          {i18n._x('Luxury mani pedi', 'footer link')}
        </Link>
      </li>,
      <li key="landing-footer-speciality-link-physical-8">
        <Link
          className="landing-footer__section-link"
          to={`/${i18n.lc}/treatments/osteopathy`}>
          {i18n._x('Osteopathy consultation & treatment', 'footer link')}
        </Link>
      </li>,
      physicalFooterLastLink,
    ]);
  }

  @computed get covidRiskAssessmentLink() {
    const {i18n} = this.context;
    if (i18n.umRegion === 'UK') {
      return (
        <li>
          <a
            className="landing-footer__section-link"
            href="https://urban.co/editorial/covid-19-risk-assessment-home-wellness-fitness-beauty-treatments/"
            target="_blank"
            rel="noopener"
          >
            COVID-19 risk assessment
          </a>
        </li>
      );
    }

    return null;
  }

  render() {
    const {i18n} = this.context;
    const {
      blogUrl,
      covidRiskAssessmentLink,
      digitalFooterLinks,
      downloadAppsLink,
      facebookUrl,
      instagramUrl,
      isBlogEnabled,
      isShopEnabled,
      linkedinUrl,
      physicalFooterLinks,
      renderCityLinks,
      shopUrl,
      showCityList,
      showFRDisclaimer,
      twitterUrl,
      zendeskLocale,
    } = this;

    return (
      <div className="landing-footer">
        <div className="container container--no-gutter">
          <div className="landing-footer__inner row">
            <div className="landing-footer__section col-md-2 col-xs-6">
              <h4 className="landing-footer__section-title">
                {i18n._x('Discover', 'Footer')}
              </h4>
              <ul>
                {covidRiskAssessmentLink}
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={`${this.faqUrl}${zendeskLocale}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('Help centre', 'Footer')}
                  </a>
                </li>
                {isBlogEnabled &&
                  <li>
                    <a
                      className="landing-footer__section-link"
                      href={blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {i18n._x('Blog', 'Footer')}
                    </a>
                  </li>
                }
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={`${HERO_BASE_URL}?utm_source=urban.co&utm_medium=website&utm_creative=footer`}
                    target="_blank" // eslint-disable-line react/jsx-no-target-blank
                  >
                    {i18n._x('Become an Urban pro', 'page menu link')}
                  </a>
                </li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/apps`}
                  >
                    {i18n._x('Mobile apps', 'Footer')}
                  </Link>
                </li>
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={
                      __DEV__ ?
                        `https://business.staging.urban.co/${i18n.safeLocale}/?utm_referrer=www.urban.co` :
                        `https://business.urban.co/${i18n.safeLocale}/?utm_referrer=www.urban.co`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('For work', 'Footer')}
                  </a>
                </li>
                {isShopEnabled && (
                  <li>
                    <a
                      className="landing-footer__section-link"
                      href={shopUrl}
                      target="_blank"
                      rel="noopener"
                      title={i18n._x('Visit the Urban Massage Shop', 'footer link')}
                    >
                      {i18n._x('Shop', 'footer link')}
                    </a>
                  </li>
                )}
                {i18n.umRegion === 'UK' ? (
                  <li>
                    <a
                      className="landing-footer__section-link"
                      href={this.communityUrl}
                      onClick={this.clickedCommunityLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Community
                    </a>
                  </li>
                ) : null }
              </ul>
            </div>
            <div className="landing-footer__section col-md-2 col-xs-6">
              <h4 className="landing-footer__section-title">{i18n._x('Company', 'Footer')}</h4>
              <ul>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/about`}>
                    {i18n._x('About', 'footer link')}
                  </Link>
                </li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/team`}>
                    {i18n._x('Team', 'footer link')}
                  </Link>
                </li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/reviews-testimonials`}>
                    {i18n._x('Reviews', 'Footer link')}
                  </Link>
                </li>
                <li><a className="landing-footer__section-link" href="https://urban.workable.com/" target="_blank" rel="noopener noreferrer">{i18n._x('Careers', 'Footer link')}</a></li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/legal`}>
                    {i18n._x('Legal', 'Footer link')}
                  </Link>
                </li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/cookies`}>
                    {i18n._x('Cookie policy', 'Footer link')}
                  </Link>
                </li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/security`}
                  >
                    {i18n._x('Security', 'footer link')}
                  </Link>
                </li>
                <li><a className="landing-footer__section-link" href={`${this.faqUrl}${zendeskLocale}`} target="_blank" rel="noopener noreferrer">{i18n._x('Contact us', 'Footer link')}</a></li>
                <li>
                  <Link
                    className="landing-footer__section-link"
                    to={`/${i18n.lc}/press`}>
                    {i18n._x('Press', 'Footer link')}
                  </Link>
                </li>
              </ul>
            </div>
            <div className="landing-footer__section col-md-3 col-xs-6">
              <h4 className="landing-footer__section-title">{i18n._x('Popular services', 'Footer')}</h4>
              <ul>
                {digitalFooterLinks}
                {physicalFooterLinks}
              </ul>
            </div>
            {((i18n.lc === 'en-gb' || i18n.lc === 'fr-fr') && showCityList) &&
              <div className="landing-footer__section col-md-2 col-xs-6">
                <h4 className="landing-footer__section-title">{i18n._x('Cities', 'Cities section footer')}</h4>
                <ul>
                  {renderCityLinks}
                </ul>
              </div>
            }
            <div className={cn(
              'landing-footer__section col-md-2',
              !showCityList && 'col-xs-6',
            )}>
              <h4 className="landing-footer__section-title">{i18n._x('Social', 'Social section footer')}</h4>
              <ul>
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('Facebook', 'footer link')}
                  </a>
                </li>
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('Twitter', 'footer link')}
                  </a>
                </li>
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('Instagram', 'footer link')}
                  </a>
                </li>
                <li>
                  <a
                    className="landing-footer__section-link"
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n._x('LinkedIn', 'footer link')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="landing-footer__copyright">
            <div className="row">
              <div className="col-md-4 landing-footer__copyright--align-left" >
                <LogoNew
                  className="landing-footer__logo" />
                <CountrySelect
                  fromTop
                  zIndex={11} />
              </div>
              <div className="col-md-4">
                <a
                  className="landing-footer__store-image"
                  href={downloadAppsLink}
                  id="js-landing-footer-download-on-the-app-store"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <ProgressiveImage
                    alt=""
                    lowResImg={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/v1495118209/home-en/appstore_en.svg', 'Appstore Image URL')}
                    height="40"
                    highResImg={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/v1495118209/home-en/appstore_en.svg', 'Appstore Image URL')}
                    highResImg2x={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/v1495118209/home-en/appstore_en.svg', 'Appstore Image URL')}
                    width="135"
                  />
                </a>
                <a
                  className="landing-footer__store-image landing-footer__store-image--no-margin"
                  href={downloadAppsLink}
                  id="js-landing-footer-download-on-the-google-play"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <ProgressiveImage
                    alt=""
                    lowResImg={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1495117099/home-en/optimised/gplay_en.png', 'PlayStore Image URL')}
                    height="40"
                    highResImg={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1495117099/home-en/optimised/gplay_en.png', 'PlayStore Image URL')}
                    highResImg2x={i18n._x('https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1495117099/home-en/optimised/gplay_en.png', 'PlayStore Image URL')}
                    width="135"
                  />
                </a>
                {i18n.region === 'GB' && (
                  <p className="landing-footer__rights">&copy; {i18n._x('Urban Massage Ltd. All rights reserved.', 'GB - Footer copyright')}</p>
                )}
                {i18n.region === 'FR' && (
                  <p className="landing-footer__rights">&copy; {i18n._x('Urban Massage SARL. All rights reserved.', 'FR - Footer copyright')}</p>
                )}
              </div>
              <div className="col-md-4 landing-footer__copyright--align-right">
                <div className="landing-footer__payments-container">
                  <div className="landing-footer__card-icon">
                    <ProgressiveImage
                      alt={i18n._x('Visa card logo', 'Footer')}
                      disableWebp
                      lowResImg={Visa}
                      height="9"
                      highResImg={Visa}
                      highResImg2x={Visa}
                      width="27"
                    />
                  </div>
                  <div className="landing-footer__card-icon landing-footer__card-icon--tall-icon">
                    <ProgressiveImage
                      alt={i18n._x('Mastercard card logo', 'Footer')}
                      disableWebp
                      lowResImg={Mastercard}
                      height="9"
                      highResImg={Mastercard}
                      highResImg2x={Mastercard}
                      width="27"
                    />
                  </div>
                  <div className="landing-footer__card-icon">
                    <ProgressiveImage
                      alt={i18n._x('American Express card logo', 'Footer')}
                      disableWebp
                      lowResImg={Amex}
                      height="9"
                      highResImg={Amex}
                      highResImg2x={Amex}
                      width="27"
                    />
                  </div>
                  <div className="landing-footer__card-icon landing-footer__card-icon--medium-icon">
                    <ProgressiveImage
                      alt={i18n._x('ApplePay logo', 'Footer')}
                      disableWebp
                      lowResImg={ApplePay}
                      height="9"
                      highResImg={ApplePay}
                      highResImg2x={ApplePay}
                      width="27"
                    />
                  </div>
                  <div className="landing-footer__card-icon landing-footer__card-icon--medium-icon">
                    <ProgressiveImage
                      alt={i18n._x('GooglePay logo', 'Footer')}
                      disableWebp
                      lowResImg={GooglePay}
                      height="9"
                      highResImg={GooglePay}
                      highResImg2x={GooglePay}
                      width="27"
                    />
                  </div>
                  <div className="landing-footer__card-icon">
                    <ProgressiveImage
                      alt={i18n._x('Paypal logo', 'Footer')}
                      disableWebp
                      lowResImg={Paypal}
                      height="9"
                      highResImg={Paypal}
                      highResImg2x={Paypal}
                      width="27"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showFRDisclaimer &&
            <div>
              <p className="landing-footer__bottom-fr-disclaimer">
                {i18n._x('Le terme « massage » ne correspond aucunement à la définition légale donnée par les dispositions règlementaires de l’article R. 4321-3 du Code de la Santé Publique (Décret n° 2004-802 du 29 juillet 2004 - JO du 8 août 2004).', 'French disclaimer footer part 1')}
                <br />
                {i18n._x('Il est rappelé que les massages médicaux, sportifs ou thérapeutiques ne peuvent être réalisés que par des médecins ou masseurs kinésithérapeutes', 'French disclaimer footer part 2')}
              </p>
            </div>
          }
        </div>
      </div>
    );
  }
}

export default LandingFooter;



// WEBPACK FOOTER //
// ./spa/Pages/Landing/Verticals/Components/LandingFooter.jsx